import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {Group} from "../communication/models/Group";
import {AngularFireDatabase, QueryFn} from "angularfire2/database";
import {UserGroup} from "../communication/models/UserGroup";
import {Message} from "../communication/models/Message";
import * as firebase from "firebase";
import * as _ from "lodash";
import {FirebaseApp} from "angularfire2";
import {EventFeatures} from "../../models/EventFeatures";
import {MediaStorageProvider} from "../media-storage/media-storage";
import {SessionService} from "../session-service";
import {DataReference} from "../../shared/DataReference";


@Injectable()
export class GroupsProvider implements OnDestroy {

  private _groupsSubscription: Subscription;

  private _groups$: BehaviorSubject<Group[]> = new BehaviorSubject<Group[]>([]);
  groups$: Observable<Group[]> = this._groups$.asObservable();

  totalUnreadGroupMessages$: Observable<number> = this._groups$
    .map(groups => groups.map(group => group.unread).reduce((acc, val) => acc + val, 0));

  constructor(private af: AngularFireDatabase,
              private firebase: FirebaseApp,
              private mediaStorage: MediaStorageProvider,
              private sessionService: SessionService) {

    this.sessionService.userSession$
      .first()
      .subscribe(session => {
        console.log('session here');
        this._loadGroupConversations(session.userId, session.eventId);
      });
  }

  private _watchUserGroups(eventId: string, userId: string) : Observable<UserGroup[]> {
    return this.af.list(DataReference.getGroupsForUser(userId), ref => ref.orderByChild(DataReference.EVENT_ID).equalTo(eventId))
      .snapshotChanges()
      .map(UserGroup.fromAngularFireSnapshots);
  }


  private _watchGroup(groupId: string) : Observable<Group> {
    return this.af.object(DataReference.getGroup(groupId))
      .snapshotChanges()
      .filter(x => x.payload.exists())
      .map(Group.fromAngularFireSnapshot);
  }

  private _loadGroupConversations(userId: string, eventId: string) {
    console.log(`Loading group conversations for ${userId} at ${eventId}`);

    this._groupsSubscription = this.af.list(DataReference.getGroupsForUser(userId),
      ref => ref.orderByChild(DataReference.EVENT_ID).equalTo(eventId))
      .snapshotChanges()
      .map(UserGroup.fromAngularFireSnapshots)
      .map(userGroups => userGroups.map(userGroup => this._watchGroup(userGroup.id).map(group => {
        group.status = userGroup.status;
        group.unread = userGroup.unread;
        return group;
      })))
      .flatMap(obs => Observable.combineLatest(obs))
      .subscribe(groups => {
        this._groups$.next(groups.sort((a,b) => {
          return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
        }));
      });
  }

  getConversationMessages(groupId, startAtTimeStamp: number, maxMessages: number = 2) : Promise<Message[]> {

    return new Promise<Message[]>((resolve, reject) => {

      this.af.database.ref(DataReference.getGroupMessagesRef(groupId))
        .orderByChild(DataReference.TIMESTAMP).endAt(startAtTimeStamp).limitToLast(maxMessages)
        .once(DataReference.Value)
        .then(snaps => {
          console.log('got some snaphots');
          let messages: Message[] = [];
          if (snaps.hasChildren()) {
            snaps.forEach(snap => {
              messages.push(Message.fromSnapshot(snap.key, snap.val()));
            });
          }
          console.log(messages);
          resolve(messages);
        }, err => {
          console.error(err);
          reject('Error loading messages');
        });

    });

  }

  watchConversationMessages(groupId: string, startAtTimeStamp: number = null, maxMessages: number = 2) : Observable<Message[]> {
    let query: QueryFn;
    if (startAtTimeStamp) {
      query = (ref) => ref.orderByChild(DataReference.TIMESTAMP).limitToLast(maxMessages).startAt(startAtTimeStamp);
    } else {
      query = (ref) => ref.orderByChild(DataReference.TIMESTAMP).limitToLast(maxMessages);
    }

    return this.af.list(DataReference.getGroupMessagesRef(groupId), query)
      .snapshotChanges()
      .map(Message.fromAngularFireSnapshots);
  }

  ngOnDestroy(): void {
    if (this._groupsSubscription) {
      this._groupsSubscription.unsubscribe();
    }
  }

  sendMessageToGroup(message: Message, groupId: string) : Promise<void> {
    return new Promise<void>((resolve, reject) => {

      let data = {};

      message.conversationId = groupId;
      message.timestamp = firebase.database.ServerValue.TIMESTAMP;
      let messageId = this.firebase.database().ref(DataReference.getConversationMessagesRef()).push().key;

      data[`${DataReference.getGroupMessagesRef(groupId)}/${messageId}`] = message;

      this.sessionService.userSession$
        .first()
        .withLatestFrom(this.sessionService.publicProfile$)
        .subscribe(([session, profile]) => {
          // server processing
          console.log('getting ready for server processing');
          let serverProcessing: any = Object.assign({}, message);
          serverProcessing['recipient'] = message.conversationId;
          serverProcessing['senderName'] = profile.displayName;

          data[DataReference.getServerProcessingGroupMessage(session.eventId, messageId)] = message;

          this.firebase.database().ref().update(data, err => {
            if (err) {
              console.error(err);
              reject('Error sending message');
            } else {
              resolve();
            }
          });
        });
    });
  }

  watchGroupParticipantIds(groupId: string) : Observable<GroupAccess[]> {
    return this.af.list(DataReference.getGroupParticipantsRef(groupId))
      .snapshotChanges()
      .map(GroupAccess.fromSnapshots);
  }

  addParticipantToGroup(groupId: string, participantId: string) : Promise<void> {
    return new Promise((resolve, reject) => {
      this.sessionService.userSession$
        .first()
        .subscribe(session => {
          let data: {[key:string]: any} = {};

          data[`${DataReference.getGroupParticipantsRef(groupId)}/${participantId}`] = GroupAccess.ACCESS_PARTICIPANT;
          data[`${DataReference.getGroupsForUser(participantId)}/${groupId}`] = {
            eventId: session.eventId,
            status: Group.STATUS_OK,
            unread: 0
          };

          this.af.database.ref().update(data, err => {
            if (err) {
              console.error(err);
              reject('Error adding member');
            } else {
              resolve();
            }
          });
        });
    });
  }

  removeParticipantFromGroup(groupId: string, userId: string) : Promise<void> {
    return new Promise((resolve, reject) => {
      let data: {[key:string]: any} = {};

      // group objects
      data[`${DataReference.getGroupParticipantsRef(groupId)}/${userId}`] = null;
      data[`${DataReference.getGroupsForUser(userId)}/${groupId}`] = null;

      this.firebase.database().ref().update(data, err => {
        if (err) {
          console.error(err);
          reject('Error adding member');
        } else {
          resolve();
        }
      });
    });
  }

  saveGroupDetails(groupDetails: Group) : Promise<string> {
    return new Promise<string>((resolve, reject) => {

      if (groupDetails.id) {
        // this is an update
        this._doSaveGroup(this._getUpdateGroupDetails(groupDetails)).then(() => {
          resolve(groupDetails.id);
        }, err => {
          reject(err);
        });
      } else {
        // this is a new group
        this.sessionService.userSession$
          .first()
          .subscribe(session => {
            let group: Group = _.cloneDeep(groupDetails);
            group.id = this.firebase.database().ref(DataReference.GROUPS).push().key;

            this._doSaveGroup(this._getNewGroupDetails(session.eventId, group)).then(() => {
              resolve(group.id);
            }, err => {
              reject(err);
            });
          });
      }

    });
  }

  private _doSaveGroup(data: {[key:string]:any}) : Promise<void> {
    return new Promise((resolve, reject) => {
      this.firebase.database().ref().update(data, err => {
        if (err) {
          console.error(err);
          reject('Error saving group');
        } else {
          resolve();
        }
      });
    });
  }

  private _getUpdateGroupDetails(groupDetails: Group) : {[key:string]:any} {
    let data: {[key:string]: any} = {};
    data[`${DataReference.getGroup(groupDetails.id)}/name`] = groupDetails.name;
    data[`${DataReference.getGroup(groupDetails.id)}/description`] = groupDetails.description;

    return data;
  }

  private _getNewGroupDetails(eventId: string, groupDetails: Group) : {[key:string]:any} {
    let userId: string = this.sessionService.userId;

    // group
    let data: {[key:string]: any} = this._getUpdateGroupDetails(groupDetails);
    data[`${DataReference.getGroup(groupDetails.id)}/creator`] = userId;

    // user objects
    data[`${DataReference.getGroupsForUser(userId)}/${groupDetails.id}`] = {
      eventId: eventId,
      status: Group.STATUS_OK
    };

    // group objects
    data[`${DataReference.getGroupParticipantsRef(groupDetails.id)}/${userId}`] = GroupAccess.ACCESS_CREATOR;

    // event objects
    data[`${DataReference.getEventGroups(eventId)}/${groupDetails.id}`] = EventFeatures.STATUS_ENABLED;

    return data;
  }

  setGroupPhoto(groupId: string, image: string) : Promise<void> {
    return new Promise((resolve, reject) => {

      let storageRef = `${MediaStorageProvider.getStorageRefForGroup(groupId)}/groupPhoto/${groupId}.jpeg`;

      this.mediaStorage.uploadPhotoToStorage(storageRef, 'image/jpeg', image).then((downloadUrl) => {
        let ref: string = DataReference.getGroup(groupId);

        let data = {};
        data[`${ref}/photo`] = downloadUrl;

        this.af.database.ref().update(data, err => {
          if (err) {
            console.error(err);
            reject('Error updating group photo');
          } else {
            resolve();
          }
        });
      });


    });
  }

  clearUnreadMessages(userId: string, groupId: string) : Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let data: {[key:string]:any} = {};
      data[`${DataReference.getGroupsForUser(userId)}/${groupId}/unread`] = 0;

      this.firebase.database().ref().update(data, err => {
        if (err) {
         console.error(err);
         reject('Error clearing unread messages');
        } else {
          resolve();
        }
      });
    });
  }
}

export class GroupAccess {

  static ACCESS_PARTICIPANT: number = 0;
  static ACCESS_ADMIN: number = 1;
  static ACCESS_CREATOR: number = 2;

  constructor(public userId: string, public access: number = 0) {
  }

  static fromSnapshots(snaps: any) : GroupAccess[] {
    return snaps.map(snap => GroupAccess._fromSnapshot(snap.key, snap.payload.val()));
  }

  private static _fromSnapshot(key: string, val: number) : GroupAccess {
    return new GroupAccess(key, val);
  }
}



// WEBPACK FOOTER //
// ./src/providers/groups/groups.ts